import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useBookingDraft } from '../features/booking/BookingDraft';
import { startTripSearch } from '../services/api/bookings';
import {
  createHostedCardSession,
  readPaymentMethods,
  selectDefaultPaymentMethod,
  type StoredPaymentMethod,
} from '../services/api/payments';
import { getCustomerAuthUser } from '../services/storage/customerFlow';
import { formatRand, isBookableVehicle } from '../config/rateCard';
import { requiresScheduledBooking, INSTANT_MAX_DISTANCE_KM } from '../config/bookingPolicy';
import '../styles/route.css';

/**
 * The last screen before money moves.
 *
 * Mirrors the app's `confirm-shift`: the route, the vehicle, the amount that
 * will be held, the card, and an explicit statement that the final fare can
 * change but never without approval. The customer should be able to answer
 * "what is about to happen and what will it cost" without scrolling.
 *
 * Three guards, all copied from the app rather than reasoned about afresh:
 * the vehicle must be one the app can dispatch, the route must be inside the
 * instant-trip limit, and there must be a saved card. Each explains itself and
 * offers the way forward rather than only refusing.
 */
const BookingConfirmPage: React.FC = () => {
  const navigate = useNavigate();
  const { draft, setDraft, isComplete } = useBookingDraft();
  const user = getCustomerAuthUser() as { id?: string | number } | null;

  const [methods, setMethods] = useState<StoredPaymentMethod[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMethods(readPaymentMethods(user));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const card = selectDefaultPaymentMethod(methods);
  const tooFar = requiresScheduledBooking(draft.distanceKm);
  const vehicleAllowed = draft.vehicleType ? isBookableVehicle(draft.vehicleType) : false;
  const canBook = isComplete && !!card && !tooFar && vehicleAllowed && !submitting;

  const handleAddCard = useCallback(async () => {
    setError(null);

    try {
      const returnUrl = `${window.location.origin}/payments/tokenized`;
      window.location.href = await createHostedCardSession(returnUrl);
    } catch (cause) {
      setError((cause as Error).message);
    }
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!canBook || !user?.id || !draft.pickup || !draft.dropoff || !card) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await startTripSearch(String(user.id), draft.idempotencyKey, {
        user_lat: draft.pickup.lat,
        user_lng: draft.pickup.lng,
        dest_lat: draft.dropoff.lat,
        dest_lng: draft.dropoff.lng,
        vehicle_type: draft.vehicleType as string,
        estimated_fare: draft.estimatedFare as number,
        payment_method: card.publicId,
        notes_for_driver: draft.notes.trim() || null,
      });

      navigate(`/book/searching?tripId=${encodeURIComponent(response.tripId)}`, { replace: true });
    } catch (cause) {
      /* Stay on this screen and keep the same idempotency key, so pressing
         Confirm again resumes the one attempt instead of starting a second. */
      setError(
        (cause as { message?: string })?.message ??
          'We could not start your trip. Please try again.',
      );
      setSubmitting(false);
    }
  }, [canBook, card, draft, navigate, user]);

  if (!isComplete) {
    return (
      <Shell>
        <p className="sw-estimate-note">
          Your trip details are missing. Start from a route estimate and we will bring the details
          through.
        </p>
        <Link className="sw-btn-primary" to="/#estimate">
          Price a route
        </Link>
      </Shell>
    );
  }

  return (
    <Shell>
      <dl className="sw-coverage-list">
        <div>
          <dt>Pickup</dt>
          <dd>{draft.pickup?.address}</dd>
        </div>
        <div>
          <dt>Drop-off</dt>
          <dd>{draft.dropoff?.address}</dd>
        </div>
        <div>
          <dt>Vehicle</dt>
          <dd>{draft.vehicleType}</dd>
        </div>
        <div>
          <dt>Estimated hold</dt>
          <dd>{draft.estimatedFare != null ? formatRand(draft.estimatedFare) : '—'}</dd>
        </div>
      </dl>

      <label className="sw-capture-label" htmlFor="booking-notes">
        Notes for the driver (optional)
      </label>
      <textarea
        className="sw-capture-input"
        id="booking-notes"
        onChange={(event) => setDraft({ notes: event.target.value })}
        placeholder="e.g. Fragile items, help needed loading"
        value={draft.notes}
      />

      {/* Guards, each with the way forward rather than a bare refusal. */}
      {tooFar ? (
        <p className="sw-estimate-error">
          This route is over {INSTANT_MAX_DISTANCE_KM} km, so it needs to be booked as a scheduled
          trip in the app rather than dispatched now.
        </p>
      ) : null}

      {!vehicleAllowed ? (
        <p className="sw-estimate-error">
          That vehicle cannot be booked yet. Choose a bakkie, or book the larger vehicle in the app.
        </p>
      ) : null}

      {card ? (
        <p className="sw-estimate-note">
          Paying with {card.brand ?? 'card'} ···· {card.last4 ?? '____'}. We reserve the estimated
          amount before finding a driver — your bank may ask for 3D Secure.
        </p>
      ) : (
        <div>
          <p className="sw-estimate-error">Add a card before booking. Card payments only.</p>
          <button className="sw-btn-secondary" onClick={() => void handleAddCard()} type="button">
            Add a card
          </button>
        </div>
      )}

      {error ? <p className="sw-estimate-error">{error}</p> : null}

      <p className="sw-estimate-note">No fare change is applied without your approval.</p>

      <button
        className="sw-btn-primary"
        disabled={!canBook}
        onClick={() => void handleConfirm()}
        type="button"
      >
        {submitting ? 'Finding a driver…' : 'Confirm and find a driver'}
      </button>
    </Shell>
  );
};

/** Page chrome only — presentational, so it holds no draft state of its own. */
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="landing-shell">
      <Header />
      <main>
        <section className="sw-section">
          <div className="container">
            <div className="sw-section-head">
              <span className="sw-eyebrow">Confirm your trip</span>
              <h1>Ready to dispatch</h1>
            </div>
            {children}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BookingConfirmPage;
